import { createThunklessAction } from 'thunkless';

import { avo } from '@/api/app/avo-client';
import type { S3PresignedPost } from '@/redux/app/reducers/templates';

export enum TemplatesTypes {
  FETCH_TEMPLATES_DATA_REQUEST = 'app/templates/FETCH_TEMPLATES_DATA_REQUEST',
  FETCH_TEMPLATES_DATA_SUCCESS = 'app/templates/FETCH_TEMPLATES_DATA_SUCCESS',
  FETCH_TEMPLATES_DATA_FAILURE = 'app/templates/FETCH_TEMPLATES_DATA_FAILURE',

  CREATE_TEMPLATE_REQUEST = 'app/templates/CREATE_TEMPLATE_REQUEST',
  CREATE_TEMPLATE_SUCCESS = 'app/templates/CREATE_TEMPLATE_SUCCESS',
  CREATE_TEMPLATE_FAILURE = 'app/templates/CREATE_TEMPLATE_FAILURE',

  UPDATE_TEMPLATE_REQUEST = 'app/templates/UPDATE_TEMPLATE_REQUEST',
  UPDATE_TEMPLATE_SUCCESS = 'app/templates/UPDATE_TEMPLATE_SUCCESS',
  UPDATE_TEMPLATE_FAILURE = 'app/templates/UPDATE_TEMPLATE_FAILURE',

  DESTROY_TEMPLATE_REQUEST = 'app/templates/DESTROY_TEMPLATE_REQUEST',
  DESTROY_TEMPLATE_SUCCESS = 'app/templates/DESTROY_TEMPLATE_SUCCESS',
  DESTROY_TEMPLATE_FAILURE = 'app/templates/DESTROY_TEMPLATE_FAILURE',

  SUBMIT_WHATSAPP_REQUEST = 'app/templates/SUBMIT_WHATSAPP_REQUEST',
  SUBMIT_WHATSAPP_SUCCESS = 'app/templates/SUBMIT_WHATSAPP_SUCCESS',
  SUBMIT_WHATSAPP_FAILURE = 'app/templates/SUBMIT_WHATSAPP_FAILURE',

  REFRESH_WHATSAPP_REQUEST = 'app/templates/REFRESH_WHATSAPP_REQUEST',
  REFRESH_WHATSAPP_SUCCESS = 'app/templates/REFRESH_WHATSAPP_SUCCESS',
  REFRESH_WHATSAPP_FAILURE = 'app/templates/REFRESH_WHATSAPP_FAILURE',

  CLEAR_TEMPLATE_FORM_ERROR = 'app/templates/CLEAR_TEMPLATE_FORM_ERROR',
}

// formStatus/formError key for a template's WhatsApp submission, kept apart from its edit form
export const whatsappFormKey = (id: string) => `whatsapp:${id}`;

export type WhatsappTemplateCategory = 'MARKETING' | 'UTILITY';

// Meta language codes, mirrored by Macro::WHATSAPP_LANGUAGES
export const WHATSAPP_TEMPLATE_LANGUAGES = {
  en: 'English',
  es: 'Spanish',
  pt_BR: 'Portuguese (Brazil)',
  fr: 'French',
  de: 'German',
  it: 'Italian',
} as const;
export type WhatsappTemplateLanguage = keyof typeof WHATSAPP_TEMPLATE_LANGUAGES;

export const submitTemplateToWhatsapp = ({
  subdomain, id, category, language,
}: { subdomain: string; id: string; category: WhatsappTemplateCategory; language: WhatsappTemplateLanguage }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.SUBMIT_WHATSAPP_REQUEST,
      TemplatesTypes.SUBMIT_WHATSAPP_SUCCESS,
      TemplatesTypes.SUBMIT_WHATSAPP_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/accounts/${subdomain}/campaigns/${id}/submit_whatsapp`,
      payload: { category, language },
    }, { retries: 0 }),
    meta: { id },
  });

export const refreshTemplateWhatsapp = ({ subdomain, id }: { subdomain: string; id: string }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.REFRESH_WHATSAPP_REQUEST,
      TemplatesTypes.REFRESH_WHATSAPP_SUCCESS,
      TemplatesTypes.REFRESH_WHATSAPP_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/accounts/${subdomain}/campaigns/${id}/refresh_whatsapp`,
    }, { retries: 0 }),
    meta: { id },
  });

export const clearTemplateFormError = (formKey: 'create' | string) => ({
  type: TemplatesTypes.CLEAR_TEMPLATE_FORM_ERROR as const,
  meta: { formKey },
});

export interface MacroPayload {
  key: string;
  raw_text: string;
  hotkey?: string;
  whatsapp_approved?: boolean;
  media_url?: string | null;
  media_mime?: string | null;
}

export const fetchTemplatesData = ({
  subdomain, page = 1, search = '',
}: { subdomain: string; page?: number; search?: string }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.FETCH_TEMPLATES_DATA_REQUEST,
      TemplatesTypes.FETCH_TEMPLATES_DATA_SUCCESS,
      TemplatesTypes.FETCH_TEMPLATES_DATA_FAILURE,
    ] as const,
    // Ordering, pagination, and search are all server-side now — the store only
    // ever holds the current page. `search` is echoed in meta so the reducer can
    // record which query the returned page belongs to.
    meta: { page, search },
    promise: () => avo.ajax({
      method: 'GET',
      route: `/accounts/${subdomain}/templates_data`,
      params: { page: String(page), search },
    }),
  });

// Fetched just-in-time when the user actually adds an attachment, not stored in
// Redux — a presigned POST is single-use and short-lived, so we mint a fresh one
// per upload instead of caching one from page load.
export const fetchTemplateImagePost = async (
  { subdomain }: { subdomain: string },
): Promise<S3PresignedPost> => {
  const res: any = await avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/templates_image_post`,
  });
  const data = res?.data;
  if (!data?.url || !data?.fields) throw new Error('Could not prepare the upload.');
  return { url: data.url, fields: data.fields };
};

// Admin-only: queues linking the inbox's WhatsApp approved templates to the approved templates in its Twilio account.
export const syncWhatsappTemplates = async ({ subdomain }: { subdomain: string }): Promise<void> => {
  await avo.ajax({
    method: 'POST',
    route: `/accounts/${subdomain}/sync_whatsapp_templates`,
  }, { retries: 0 });
};

export const createTemplate = ({
  subdomain, macro,
}: { subdomain: string; macro: MacroPayload }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.CREATE_TEMPLATE_REQUEST,
      TemplatesTypes.CREATE_TEMPLATE_SUCCESS,
      TemplatesTypes.CREATE_TEMPLATE_FAILURE,
    ] as const,
    // retries: 0 — validation errors (duplicate name, etc.) shouldn't silently retry
    promise: () => avo.ajax({
      method: 'POST',
      route: `/accounts/${subdomain}/macros`,
      payload: { macro, ajax: 'true', react_request: 'true' },
    }, { retries: 0 }),
  });

export const updateTemplate = ({
  subdomain, id, macro,
}: { subdomain: string; id: string; macro: Partial<MacroPayload> }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.UPDATE_TEMPLATE_REQUEST,
      TemplatesTypes.UPDATE_TEMPLATE_SUCCESS,
      TemplatesTypes.UPDATE_TEMPLATE_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'PUT',
      route: `/accounts/${subdomain}/campaigns/${id}`,
      payload: { macro, ajax: 'true', react_request: 'true' },
    }, { retries: 0 }),
    meta: { id },
  });

export const destroyTemplate = ({
  subdomain, id,
}: { subdomain: string; id: string }) =>
  createThunklessAction({
    type: [
      TemplatesTypes.DESTROY_TEMPLATE_REQUEST,
      TemplatesTypes.DESTROY_TEMPLATE_SUCCESS,
      TemplatesTypes.DESTROY_TEMPLATE_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'DELETE',
      route: `/accounts/${subdomain}/campaigns/${id}`,
      payload: { ajax: 'true', react_request: 'true' },
    }, { retries: 0 }),
    meta: { id },
  });
